/**
 * notificationSystem.js — Legacy notification API, now delegates to UnifiedNotificationContext.
 *
 * This module maintains backward compatibility for existing showNotification() calls.
 * New code should use useUnifiedNotifications() hook directly.
 *
 * The NotificationContainer is deprecated — notifications now render in NotificationCenter.
 */
import React, { useState, useEffect } from 'react';
import { CircleCheck, CircleX, Info } from 'lucide-react';
import { notificationDebounce } from '../globals';

// Re-export showNotification from unified context
export { showNotification } from '../contexts/UnifiedNotificationContext';

// Global notification state (legacy)
let notificationListeners = [];
let notificationId = 0;
const lastShown = new Map();

// Legacy showNotification for fallback (deprecated)
export const showNotificationLegacy = (message, type = 'success', duration = 4000, options = {}) => {
  const { key, onClick } = options;
  if (key) {
    const now = Date.now();
    const lastTime = lastShown.get(key);
    if (lastTime && now - lastTime < notificationDebounce) {
      return; // Debounce notification
    }
    lastShown.set(key, now);
  }

  const notification = {
    id: notificationId++,
    message,
    type,
    duration,
    onClick
  };
  
  notificationListeners.forEach(listener => listener(notification));
};

export const NotificationContainer = () => {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const handleNotification = (notification) => {
      setNotifications(prev => [...prev, notification]);
      
      // Auto-remove after duration
      setTimeout(() => {
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      }, notification.duration);
    };

    notificationListeners.push(handleNotification);
    
    return () => {
      notificationListeners = notificationListeners.filter(l => l !== handleNotification);
    };
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CircleCheck className="notification-icon-success" />;
      case 'error':
        return <CircleX className="notification-icon-error" />;
      case 'warning':
        return <Info className="notification-icon-warning" />;
      default:
        return <Info className="notification-icon-info" />;
    }
  };

  return (
    <div className="notification-container">
      {notifications.map(notification => (
        <div 
          key={notification.id} 
          className={`notification notification-${notification.type}${notification.onClick ? ' notification-clickable' : ''}`}
          onClick={() => {
            if (notification.onClick) {
              notification.onClick();
            }
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
          }}
        >
          <div className="notification-icon">
            {getIcon(notification.type)}
          </div>
          <div className="notification-message">
            {notification.message}
          </div>
        </div>
      ))}
    </div>
  );
};